import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { convertUnits, returnUnit } from './utils/kiln-card-utils';

/**
 * @param {Object} props A prop drilled down from LogKiln.js
 * @param {(kilnDetails: {Object}) => void} props.setKilnDetails This sets the current kilnDetails state to be added to the project list.
 * @param {Object} props.kilnDetails Listens to changes to kilnDetails, and resets selected kiln card if submission was successful.
 * @returns {JSX.Element} Returns a Kiln Card component used in Flame Cap Kiln -> Project Info. The card has preset values to help streamline
 * the user's dimension selection process.
 */
export default function RofCard({ setKilnDetails, kilnDetails }) {
    const [isActive, setIsActive] = useState(null)
    const { selectedProject } = useSelector(state => state.postFormData);
    const currentProjectMeasurementSystem = selectedProject.measureType

    // listen to changes in kilnDetails, and reset if submission has taken place
    useEffect(() => {
        if (kilnDetails.kilnId === null) {
            setIsActive(null)
        }
    }, [kilnDetails])

    const kilnCardDetailsFontSize = 18

    const kilnPanelConstants = {
        kilnType: 'Ring of Fire',
        units: returnUnit(currentProjectMeasurementSystem),
        heightOfInnerPanels: convertUnits(1.02, 'Metric', currentProjectMeasurementSystem),
        heatShieldPanelLength: convertUnits(0.91, 'Metric', currentProjectMeasurementSystem),
        heatShieldPanelWidth: convertUnits(1.35, 'Metric', currentProjectMeasurementSystem),
    }

    const kilnCardObjects = [
        {
            kilnId: "5_panel",
            numberOfPanels: 5,
            diameter: convertUnits(1.94, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "6_panel",
            numberOfPanels: 6,
            diameter: convertUnits(2.33, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "7_panel",
            numberOfPanels: 7,
            diameter: convertUnits(2.72, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "8_panel",
            numberOfPanels: 8,
            diameter: convertUnits(3.10, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "9_panel",
            numberOfPanels: 9,
            diameter: convertUnits(3.49, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "10_panel",
            numberOfPanels: 10,
            diameter: convertUnits(3.88, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "11_panel",
            numberOfPanels: 11,
            diameter: convertUnits(4.27, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
        {
            kilnId: "12_panel",
            numberOfPanels: 12,
            diameter: convertUnits(4.66, 'Metric', currentProjectMeasurementSystem),
            ...kilnPanelConstants
        },
    ]

    return (
        <Box sx={{
            width: '100%',
            maxWidth: '1500px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            rowGap: 3,
            columnGap: 1,
            p: 4
        }}>
            {
                kilnCardObjects.map((kilnCardObject, index) => (
                    <Box key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: 'fit-content',
                            textAlign: 'left',
                            color: '#000',
                            border: '1px solid #000',
                            borderRadius: '5px',
                            padding: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                            },
                            ...(isActive === index && {
                                background: 'linear-gradient(to bottom right, #535988, #313551)',
                                color: '#FFF', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px'
                            })

                        }}
                        onClick={() => {
                            setIsActive(index);
                            setKilnDetails(kilnCardObject);
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 3, gap: 1 }}>
                            <Typography sx={{ fontSize: 25, pt: 2, pb: 2, fontWeight: 'bold' }}>
                                Kiln Panels: {kilnCardObject.numberOfPanels}
                            </Typography>
                            <img src='/kiln-image.png' alt='Kiln' height={80} style={{ objectFit: 'contain' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Diameter:</Typography>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                {kilnCardObject.diameter} {returnUnit(kilnCardObject.units)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Height of Inner Panels: </Typography>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                {kilnCardObject.heightOfInnerPanels} {returnUnit(kilnCardObject.units)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Heat Shield Panel Length:</Typography>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                {kilnCardObject.heatShieldPanelLength} {returnUnit(kilnCardObject.units)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize }}>Heat Shield Panel Width:</Typography>
                            <Typography sx={{ fontSize: kilnCardDetailsFontSize, fontWeight: 'bold' }}>
                                {kilnCardObject.heatShieldPanelWidth} {returnUnit(kilnCardObject.units)}
                            </Typography>
                        </Box>
                    </Box>
                ))
            }
        </Box>
    )
}
